import { PageController } from '@client/components/_core/layout/PageController';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { match } from 'ts-pattern';
import { type TaskDefinition, TaskDefinitionForm } from './TaskDefinitionForm';

export const Route = createFileRoute(
    '/console/$orgId/configuration/claims-products/claims/task-lists/search/$taskListId/tasks/edit/$id',
)({
    staticData: {
        metadata: {
            title: 'Edit task',
        },
        ui: {
            title: 'Edit task',
        },
        presentation: 'overlay',
        modules: ['claim'],
    },
    params: {
        parse: ({ id }) => ({
            id: BigInt(id),
        }),
    },
    component: EditTaskDefinition,
});

function EditTaskDefinition() {
    const { id: taskDefinitionId, taskListId } = Route.useParams();
    const { trpc, trpcUtils } = useTrpc();
    const navigate = useNavigate();
    const orgId = useOrgId();
    const [open, setOpen] = useState(true);
    const { data: taskDefinition, isPending: isLoading } = trpc.taskDefinition.getTaskDefinition.useQuery(
        {
            id: taskDefinitionId,
        },
        { refetchOnWindowFocus: false },
    );
    const { mutate: updateTaskDefinition, isPending: isUpdating } =
        trpc.taskDefinition.updateTaskDefinition.useMutation({
            onError(error) {
                errorMessage.show(error);
            },
        });

    const handleSubmit = (values: TaskDefinition) => {
        if (!taskDefinitionId) return;
        updateTaskDefinition(
            {
                id: taskDefinitionId,
                set: {
                    name: values.name ?? '',
                    description: values.description ?? '',
                    dueDateOffset: values.dueDateOffset ?? 1,
                    reminderOffsets: values.reminderOffsets,
                },
            },
            {
                onSuccess: () => {
                    message.success('Task updated');
                    trpcUtils.taskDefinition.listTaskDefinitions.invalidate();
                    setOpen(false);
                },
            },
        );
    };
    return (
        <PageController route={Route}>
            <TaskDefinitionForm
                open={open}
                mode="edit"
                data={taskDefinition}
                state={match({ isLoading, isUpdating })
                    .with({ isLoading: true }, () => 'loading' as const)
                    .with({ isUpdating: true }, () => 'submitting' as const)
                    .otherwise(() => 'ready' as const)}
                onSubmit={handleSubmit}
                onCloseFinished={() => {
                    navigate({
                        to: '/console/$orgId/configuration/claims-products/claims/task-lists/search/$taskListId/tasks',
                        params: { orgId, taskListId },
                    });
                }}
            />
        </PageController>
    );
}
